import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";

// ── Validation Helpers ──────────────────────────────────────────────────────
const validate = (fields) => {
  const errors = {};

  if (!fields.name.trim()) {
    errors.name = "Name is required.";
  } else if (fields.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters.";
  } else if (!/^[a-zA-Z\s.'-]+$/.test(fields.name.trim())) {
    errors.name = "Name can only contain letters, spaces, dots, hyphens and apostrophes.";
  }

  if (!fields.mobile.trim()) {
    errors.mobile = "Mobile number is required.";
  } else if (!/^[6-9]\d{9}$/.test(fields.mobile.trim())) {
    errors.mobile = "Enter a valid 10-digit Indian mobile number.";
  }

  if (!fields.age.trim()) {
    errors.age = "Age is required.";
  } else {
    const age = Number(fields.age);
    if (!Number.isInteger(age) || age < 1 || age > 120) {
      errors.age = "Age must be between 1 and 120.";
    }
  }

  if (!fields.totalMembers.trim()) {
    errors.totalMembers = "Total members is required.";
  } else {
    const members = Number(fields.totalMembers);
    if (!Number.isInteger(members) || members < 1 || members > 500) {
      errors.totalMembers = "Total members must be between 1 and 500.";
    }
  }

  if (!fields.date) {
    errors.date = "Date is required.";
  } else {
    const selected = new Date(fields.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selected < today) {
      errors.date = "Date cannot be in the past.";
    }
  }

  if (!fields.time) {
    errors.time = "Time is required.";
  }

  return errors;
};

// ── Input Field Component ────────────────────────────────────────────────────
const FormField = ({ label, id, error, children, required }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-semibold text-gray-700 mb-1"
    >
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        {error}
      </p>
    )}
  </div>
);

const inputClass = (hasError) =>
  `w-full px-4 py-3 rounded-xl border text-sm transition-all duration-200 outline-none focus:ring-2 ${
    hasError
      ? "border-red-400 bg-red-50 focus:ring-red-200 focus:border-red-500"
      : "border-gray-200 bg-white focus:ring-amber-200 focus:border-amber-500 hover:border-amber-300"
  }`;

// ── Main Component ────────────────────────────────────────────────────────────
const EventRegisterPage = () => {
  const initialForm = {
    name: "",
    mobile: "",
    age: "",
    totalMembers: "",
    date: "",
    time: "",
  };

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [firebaseError, setFirebaseError] = useState("");

  // Today's date in YYYY-MM-DD for min date attribute
  const todayStr = new Date().toISOString().split("T")[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Live validate touched fields
    if (touched[name]) {
      const newErrors = validate({ ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: newErrors[name] }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const newErrors = validate(formData);
    setErrors((prev) => ({ ...prev, [name]: newErrors[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mark all fields as touched
    const allTouched = Object.keys(initialForm).reduce(
      (acc, k) => ({ ...acc, [k]: true }),
      {}
    );
    setTouched(allTouched);

    const validationErrors = validate(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    setFirebaseError("");

    try {
      await addDoc(collection(db, "leads"), {
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
        age: Number(formData.age),
        totalMembers: Number(formData.totalMembers),
        date: formData.date,
        time: formData.time,
        createdAt: serverTimestamp(),
        source: "event-register-form",
      });
      setSubmitted(true);
      setFormData(initialForm);
      setTouched({});
      setErrors({});
    } catch (err) {
      console.error("Firestore error:", err);
      setFirebaseError(
        "Something went wrong while submitting. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success State ────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#fdf6ec] flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-3">
            You're Registered! 🎉
          </h2>
          <p className="text-gray-500 text-base mb-8">
            Thank you for registering. We'll get in touch with you shortly to
            confirm your booking.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="inline-block px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-colors duration-200 shadow-md"
          >
            Register Another
          </button>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-[#fdf6ec] py-16 px-4"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Header */}
      <div className="text-center mb-10">
        <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">
          Event Registration
        </span>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 leading-tight">
          Register for the{" "}
          <span className="text-amber-500">Event</span>
        </h1>
        <p className="mt-4 text-gray-500 text-base max-w-md mx-auto">
          Fill in your details below to secure your spot at our upcoming
          Barbequeen event.
        </p>
      </div>

      {/* Card */}
      <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* Decorative top bar */}
        <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />

        <form onSubmit={handleSubmit} noValidate className="p-8 space-y-6">
          {/* Firebase error */}
          {firebaseError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {firebaseError}
            </div>
          )}

          {/* Name */}
          <FormField label="Full Name" id="name" error={errors.name} required>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="e.g. Rahul Kumar"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass(!!errors.name)}
              autoComplete="name"
            />
          </FormField>

          {/* Mobile */}
          <FormField label="Mobile Number" id="mobile" error={errors.mobile} required>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium select-none">
                +91
              </span>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                placeholder="9876543210"
                maxLength={10}
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`${inputClass(!!errors.mobile)} pl-12`}
                autoComplete="tel"
              />
            </div>
          </FormField>

          {/* Age + Total Members side by side */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Age" id="age" error={errors.age} required>
              <input
                id="age"
                name="age"
                type="number"
                placeholder="e.g. 28"
                min={1}
                max={120}
                value={formData.age}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass(!!errors.age)}
              />
            </FormField>

            <FormField label="Total Members" id="totalMembers" error={errors.totalMembers} required>
              <input
                id="totalMembers"
                name="totalMembers"
                type="number"
                placeholder="e.g. 4"
                min={1}
                max={500}
                value={formData.totalMembers}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass(!!errors.totalMembers)}
              />
            </FormField>
          </div>

          {/* Date + Time side by side */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Event Date" id="date" error={errors.date} required>
              <input
                id="date"
                name="date"
                type="date"
                min={todayStr}
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass(!!errors.date)}
              />
            </FormField>

            <FormField label="Preferred Time" id="time" error={errors.time} required>
              <input
                id="time"
                name="time"
                type="time"
                value={formData.time}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass(!!errors.time)}
              />
            </FormField>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-base rounded-2xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Submitting…
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Register Now
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-400">
            By registering, you agree to our{" "}
            <a href="/terms-and-conditions" className="text-amber-600 hover:underline">
              Terms & Conditions
            </a>
            .
          </p>
        </form>
      </div>
    </div>
  );
};

export default EventRegisterPage;

